import _defineProperty from "/opt/build/repo/node_modules/@babel/runtime/helpers/esm/defineProperty.js";
function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }
function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }
import { shallowEqual, useSelector, useDispatch } from 'react-redux';
import { useMemo, useEffect } from 'react';
import { bindActionCreators } from 'redux';
import { loadSubscription, skipSubscription, cancelSubscription, updateSubscription, pauseSubscription, resumeSubscription } from '../redux/actions';
/**
 * The `useSubscription` hook returns the current loaded subscription and
 * functions for managing the subscription.
 *
 * This function has a side effect that will automatically start loading the
 * subscription, if needed, upon initialization.
 *
 * @example
 *
 * ```javascript
 * import { useSubscription } from '@chordcommerce/react-autonomy'
 *
 * export default () => {
 *   const { subscription, isFetching } = useSubscription({ subscriptionId: 10000 })
 *
 *   if (isFetching) {
 *     return (
 *       <p>Loading…</p>
 *     )
 *   }
 *
 *   return (
 *     <p>Your subscription will be renewed on {subscription.actionableDate}</p>
 *   )
 * }
 * ```
 *
 * @category Hook
 * @param {Object} props
 * @param {number | string} props.subscriptionId - The ID of the subscription to load and manage.
 * @returns {UseSubscription}
 */
export function useSubscription(_ref) {
  var _subscriptionsState$l, _subscriptionsState$s, _subscriptionsState$e, _subscriptionsState$d;
  var subscriptionId = _ref.subscriptionId;
  var id = Number(subscriptionId);
  var subscriptionsState = useSelector(function (state) {
    return state.subscriptions;
  }, shallowEqual);
  var isFetching = !!((_subscriptionsState$l = subscriptionsState.loading) !== null && _subscriptionsState$l !== void 0 && _subscriptionsState$l.includes(id));
  var isLoaded = !!((_subscriptionsState$s = subscriptionsState.success) !== null && _subscriptionsState$s !== void 0 && _subscriptionsState$s.includes(id));
  var error = (_subscriptionsState$e = subscriptionsState.error) === null || _subscriptionsState$e === void 0 ? void 0 : _subscriptionsState$e[id];
  var dispatch = useDispatch();
  var actionDispatches = useMemo(function () {
    return bindActionCreators({
      loadSubscription: loadSubscription(id),
      skipSubscription: skipSubscription(id),
      cancelSubscription: cancelSubscription(id),
      updateSubscription: updateSubscription(id),
      pauseSubscription: pauseSubscription(id),
      resumeSubscription: resumeSubscription(id)
    }, dispatch);
  }, [dispatch, id]);
  useEffect(function () {
    if (!id || isFetching || isLoaded || error) return;
    if (Object.keys(subscriptionsState.data).length === 0) {
      actionDispatches.loadSubscription();
    }
  }, [subscriptionsState, id, actionDispatches.loadSubscription]);
  return _objectSpread({
    subscription: (_subscriptionsState$d = subscriptionsState.data) === null || _subscriptionsState$d === void 0 ? void 0 : _subscriptionsState$d[id],
    error: error,
    isLoaded: isLoaded,
    isFetching: isFetching
  }, actionDispatches);
}